import React, { useCallback } from 'react';
import { ChevronLeft, Download, Info, AlertTriangle, Clock } from 'lucide-react';

/*
 * Funnel Conversion Report display (Carlos §4.4, Phase 2 wrap). Pure presentation
 * over the aggregate payload from reports:conversion:run — counts / % / days only,
 * an org-level rollup, NO patient data, NO revenue, NO per-rep breakout. Four
 * sections:
 *   1. Stage-to-stage conversion table (adjacent milestone pairs). When a period
 *      comparison is present, each row also shows the prior window's % + pp delta.
 *   2. Time-in-stage + velocity — avg/median days per stage and overall journey
 *      velocity, each with an explicit n= (post-deploy, non-backfill moves only).
 *   3. Period comparison — surfaced via the deltas in §1 and the prior range in
 *      the header; each window carries its own tracking-start approximation flag.
 *   4. By-source cross-cut — Lead→Deposit / Lead→Therapy % by referral source.
 * Tracking-start, duration-sample, and unmapped notes are rendered verbatim from
 * the sibling reports. CSV is built client-side from the returned aggregate (no
 * new data access → no re-audit), covering all four sections.
 */

const card = { background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };
const btn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #DEE2E6', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#495057' };
const sectionTitle = { fontSize: 13, fontWeight: 700, color: '#2E8B98', margin: '0 0 8px' };
const num = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
// Day/stat cell: null (no sample) → em dash, else "N.N d".
const days = (v) => (v == null ? '—' : `${v} d`);
// Signed pp delta with a leading + on gains (null → em dash).
const pp = (v) => (v == null ? '—' : `${v > 0 ? '+' : ''}${v}`);

const BY_SOURCE_COLS = [
  { key: 'leads', label: 'Leads' },
  { key: 'deposits', label: 'Deposits' },
  { key: 'therapies', label: 'Therapies' },
  { key: 'leadToDeposit', label: 'Lead→Deposit', pct: true },
  { key: 'leadToTherapy', label: 'Lead→Therapy', pct: true },
];

export default function ConversionReport({ data, onBack }) {
  const exportCsv = useCallback(() => {
    if (!data) return;
    const q = (s) => `"${String(s ?? '').replace(/"/g, '""')}"`;
    const lines = [];
    const cmp = data.comparison;

    // §1 stage-to-stage (+ comparison columns when present)
    lines.push(q('Section 1 — Stage-to-stage conversion'));
    const h1 = ['From', 'To', 'Entered prior', 'Reached next', 'Conversion %'];
    if (cmp) h1.push('Prior conversion %', 'Δ pp');
    lines.push(h1.map(q).join(','));
    for (const r of data.stageToStage || []) {
      const row = [r.fromBucket, r.toBucket, r.enteredPrior, r.reachedNext, `${r.conversionPct}%`];
      if (cmp) row.push(r.priorConversionPct == null ? '' : `${r.priorConversionPct}%`, pp(r.deltaPp));
      lines.push(row.map(q).join(','));
    }
    lines.push('');

    // §2 time-in-stage + velocity
    lines.push(q('Section 2 — Time in stage (post-deploy, non-backfill moves only)'));
    lines.push(['Stage', 'Avg days', 'Median days', 'n'].map(q).join(','));
    for (const r of data.timeInStage || []) {
      lines.push([r.bucket, r.avgDays == null ? '' : r.avgDays, r.medianDays == null ? '' : r.medianDays, r.n].map(q).join(','));
    }
    lines.push('');
    lines.push(q('Velocity (completed journeys)'));
    lines.push(['Journey', 'Avg days', 'Median days', 'n'].map(q).join(','));
    const v = data.velocity || {};
    for (const [label, key] of [['New Lead → Deposit', 'leadToDeposit'], ['New Lead → Therapy', 'leadToTherapy']]) {
      const s = v[key] || {};
      lines.push([label, s.avgDays == null ? '' : s.avgDays, s.medianDays == null ? '' : s.medianDays, s.n ?? 0].map(q).join(','));
    }
    lines.push('');

    // §4 by-source cross-cut
    lines.push(q('Section 4 — Conversion by source'));
    lines.push(['Source', ...BY_SOURCE_COLS.map((c) => c.label)].map(q).join(','));
    const fmt = (r, c) => (c.pct ? `${r[c.key]}%` : r[c.key]);
    for (const r of data.bySource || []) lines.push([r.sourceName, ...BY_SOURCE_COLS.map((c) => fmt(r, c))].map(q).join(','));
    if (data.bySourceTotals) lines.push([data.bySourceTotals.sourceName, ...BY_SOURCE_COLS.map((c) => fmt(data.bySourceTotals, c))].map(q).join(','));

    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'funnel_conversion.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  }, [data]);

  if (!data) return null;
  const {
    stageToStage = [], timeInStage = [], velocity = {}, bySource = [], bySourceTotals,
    trackingStart, approximated, unmapped, comparison,
  } = data;

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={btn} onClick={onBack}><ChevronLeft size={14} /> Back</button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#212529' }}>Funnel conversion</div>
        <span style={{ fontSize: 12, color: '#ADB5BD' }}>
          cohort of {num(data.cohortSize)} · {fmtDate(data.range?.from)} → {data.range?.to ? fmtDate(data.range.to) : 'now'}
          {comparison && <> · vs {fmtDate(comparison.range?.from)} → {fmtDate(comparison.range?.to)}</>}
        </span>
        <div style={{ marginLeft: 'auto' }}><button style={btn} onClick={exportCsv}><Download size={13} /> Export CSV</button></div>
      </div>

      {/* Tracking-start approximation note (verbatim from P1b) — current window */}
      {approximated && (
        <div style={{ ...card, background: '#FFF8E6', border: '1px solid #FDE68A', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Info size={18} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: '#7C5E10', lineHeight: 1.5 }}>
            <strong>Approximation for part of this range.</strong> True cohort progression is recorded only from{' '}
            <strong>{trackingStart ? fmtDate(trackingStart) : '(no live transitions recorded yet)'}</strong> onward
            (Stage History deploy). Leads created before then show their <em>current</em> stage as their furthest
            position, which understates mid-funnel drop-off that happened pre-recording.
          </div>
        </div>
      )}
      {comparison?.approximated && (
        <div data-testid="comparison-approx-note" style={{ ...card, background: '#FFF8E6', border: '1px solid #FDE68A', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Info size={18} color="#B45309" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 13, color: '#7C5E10', lineHeight: 1.5 }}>
            <strong>The comparison window is also partly approximated.</strong> The prior period
            ({fmtDate(comparison.range?.from)} → {fmtDate(comparison.range?.to)}) starts before stage-history
            recording began, so its pre-recording positions are current-stage approximations too.
          </div>
        </div>
      )}

      {/* ── §1 Stage-to-stage conversion ─────────────────────────────────────── */}
      <div data-testid="section-stage-to-stage" style={{ ...card, marginBottom: 12, overflowX: 'auto' }}>
        <div style={sectionTitle}>Stage-to-stage conversion</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: comparison ? 720 : 520 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: '#6C757D', fontSize: 12 }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Transition</th>
              <th style={{ padding: '6px 8px' }}>Entered prior</th>
              <th style={{ padding: '6px 8px' }}>Reached next</th>
              <th style={{ padding: '6px 8px' }}>Conversion %</th>
              {comparison && <th style={{ padding: '6px 8px' }}>Prior %</th>}
              {comparison && <th style={{ padding: '6px 8px' }}>Δ pp</th>}
            </tr>
          </thead>
          <tbody>
            {stageToStage.map((r) => (
              <tr key={`${r.fromBucket}->${r.toBucket}`} style={{ borderTop: '1px solid #F1F3F5', textAlign: 'right' }}>
                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#212529' }}>{r.fromBucket} → {r.toBucket}</td>
                <td style={{ padding: '8px' }}>{num(r.enteredPrior)}</td>
                <td style={{ padding: '8px' }}>{num(r.reachedNext)}</td>
                <td style={{ padding: '8px', fontWeight: 600 }}>{r.conversionPct}%</td>
                {comparison && <td style={{ padding: '8px', color: '#6C757D' }}>{r.priorConversionPct == null ? '—' : `${r.priorConversionPct}%`}</td>}
                {comparison && (
                  <td data-testid="stage-delta" style={{ padding: '8px', fontWeight: 600, color: r.deltaPp == null ? '#6C757D' : r.deltaPp > 0 ? '#2E7D32' : r.deltaPp < 0 ? '#C0392B' : '#6C757D' }}>
                    {pp(r.deltaPp)}
                  </td>
                )}
              </tr>
            ))}
            {stageToStage.length === 0 && (
              <tr><td colSpan={comparison ? 6 : 4} style={{ padding: 16, textAlign: 'center', color: '#ADB5BD' }}>No progression buckets configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ── §2 Time-in-stage + velocity ──────────────────────────────────────── */}
      <div data-testid="section-time-velocity" style={{ ...card, marginBottom: 12, overflowX: 'auto' }}>
        <div style={sectionTitle}>Time in stage &amp; velocity</div>

        {/* Duration-sample note: backfill excluded + small-sample honesty */}
        <div data-testid="duration-note" style={{ background: '#F0F4F8', border: '1px solid #D6E0EA', borderRadius: 8, padding: '8px 12px', marginBottom: 10, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <Clock size={16} color="#5B6B7B" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#4A5A6A', lineHeight: 1.5 }}>
            Durations use <strong>only post-deploy stage moves</strong> — approximated/backfill transitions are never
            timed. Each stat shows its sample size (<strong>n=</strong>); a small n means only a few leads have moved
            through that stage since recording began, so read those numbers as early signal, not a stable rate.
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 460 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: '#6C757D', fontSize: 12 }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Stage</th>
              <th style={{ padding: '6px 8px' }}>Avg days</th>
              <th style={{ padding: '6px 8px' }}>Median days</th>
              <th style={{ padding: '6px 8px' }}>n</th>
            </tr>
          </thead>
          <tbody>
            {timeInStage.map((r) => (
              <tr key={r.bucket} style={{ borderTop: '1px solid #F1F3F5', textAlign: 'right' }}>
                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#212529' }}>{r.bucket}</td>
                <td style={{ padding: '8px' }}>{days(r.avgDays)}</td>
                <td style={{ padding: '8px' }}>{days(r.medianDays)}</td>
                <td style={{ padding: '8px', color: '#6C757D' }}>n={num(r.n)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Velocity tiles — overall journey speed for completed journeys */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
          {[['New Lead → Deposit', velocity.leadToDeposit], ['New Lead → Therapy', velocity.leadToTherapy]].map(([label, s]) => (
            <div key={label} style={{ ...card, flex: 1, minWidth: 200, boxShadow: 'none', border: '1px solid #E9ECEF' }}>
              <div style={{ fontSize: 12, color: '#6C757D' }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#2E8B98' }}>{days((s || {}).medianDays)} <span style={{ fontSize: 12, fontWeight: 400, color: '#ADB5BD' }}>median</span></div>
              <div style={{ fontSize: 12, color: '#6C757D', marginTop: 2 }}>avg {days((s || {}).avgDays)} · <span>n={num((s || {}).n)}</span></div>
            </div>
          ))}
        </div>
      </div>

      {/* ── §4 By-source cross-cut ───────────────────────────────────────────── */}
      <div data-testid="section-by-source" style={{ ...card, marginBottom: 12, overflowX: 'auto' }}>
        <div style={sectionTitle}>Conversion by source</div>
        <div style={{ fontSize: 12, color: '#6C757D', marginBottom: 8 }}>
          Lead→Deposit / Lead→Therapy by referral source. Revenue-by-source lives in the Lead Source report — this
          cross-cut is conversion only.
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: '#6C757D', fontSize: 12 }}>
              <th style={{ padding: '6px 8px', textAlign: 'left' }}>Source</th>
              {BY_SOURCE_COLS.map((c) => <th key={c.key} style={{ padding: '6px 8px' }}>{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {bySource.map((r) => (
              <tr key={r.source == null ? 'unsourced' : r.source} style={{ borderTop: '1px solid #F1F3F5', textAlign: 'right' }}>
                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#212529' }}>{r.sourceName}</td>
                {BY_SOURCE_COLS.map((c) => <td key={c.key} style={{ padding: '8px' }}>{c.pct ? `${r[c.key]}%` : num(r[c.key])}</td>)}
              </tr>
            ))}
            {bySourceTotals && (
              <tr style={{ borderTop: '2px solid #DEE2E6', textAlign: 'right', fontWeight: 700, background: '#F8F9FA' }}>
                <td style={{ padding: '8px', textAlign: 'left' }}>{bySourceTotals.sourceName}</td>
                {BY_SOURCE_COLS.map((c) => <td key={c.key} style={{ padding: '8px' }}>{c.pct ? `${bySourceTotals[c.key]}%` : num(bySourceTotals[c.key])}</td>)}
              </tr>
            )}
            {bySource.length === 0 && (
              <tr><td colSpan={BY_SOURCE_COLS.length + 1} style={{ padding: 16, textAlign: 'center', color: '#ADB5BD' }}>No leads in this range.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Unmapped footnote (verbatim from P1b/§4.5) */}
      {unmapped?.count > 0 && (
        <div style={{ ...card, background: '#FDECEA', border: '1px solid #F5C6C0', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <AlertTriangle size={18} color="#C0392B" style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: 12, color: '#7B241C', lineHeight: 1.5 }}>
            <strong>{num(unmapped.count)} lead{unmapped.count === 1 ? '' : 's'} unmapped.</strong> Their current stage has
            no funnel-bucket mapping (still counted under each source's Leads):{' '}
            {unmapped.stages.map((s, i) => (<span key={s.stage}>{i > 0 ? ', ' : ''}<code>{s.stage}</code> ({s.count})</span>))}.
          </div>
        </div>
      )}
    </div>
  );
}
